import { useEffect, useRef } from "react";

/**
 * Returns a stable function that debounces calls to `fn` by `delayMs`.
 * The latest `fn` reference is captured via ref so the debounced call always
 * invokes the most recent version. On unmount, any pending call is flushed so
 * navigating away preserves the last value.
 */
export function useDebouncedCallback<T>(
  fn: (value: T) => void,
  delayMs: number
): (value: T) => void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<{ value: T } | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        if (pendingRef.current) {
          fnRef.current(pendingRef.current.value);
        }
      }
    };
  }, []);

  return (value: T) => {
    pendingRef.current = { value };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) fnRef.current(pending.value);
    }, delayMs);
  };
}
